import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { User } from '../types/models';
import { authService } from '../services/authService';
import { supabase } from '../supabaseClient';
import { FullPageSpinner } from '../components/common/Spinner';

interface AuthContextType {
  user: User | null;
  isAuthLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  useEffect(() => {
    const checkInitialSession = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch (e) {
        console.error("Błąd przy sprawdzaniu sesji początkowej", e);
        setUser(null);
      } finally {
        setIsAuthLoading(false);
      }
    };
    
    checkInitialSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const currentUser = session?.user ? await authService.getCurrentUser() : null;
        setUser(currentUser);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const value = { user, isAuthLoading };

  if (isAuthLoading) {
    return <FullPageSpinner />;
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};